import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, Database, Plus, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { renderAILines } from '@/lib/formatAIText';
import { FactorsConsidered } from './FactorsConsidered';
import { AlternativeSuppliers } from './AlternativeSuppliers';

const PRESET_VARIABLES = [
  'Geopolitical & Trade Risks',
  'Supply Chain Disruptions',
  'Commodity Price Trends',
  'Regulatory & Compliance Changes',
  'Sustainability & ESG Factors',
  'Currency & FX Risk',
  'Technology & Innovation Trends',
  'Inflation & Economic Outlook',
];

/**
 * Extracted from CategoryConsole.js (component-split refactor, round 2).
 * Owns the full AI Category Strategy flow: variable selection, generation,
 * save-to-reports, and the rendered strategy output. `resetKey` increments
 * on page refresh so a stale strategy is cleared against refreshed data.
 */
export function StrategySection({ canGenerateStrategy, filters, cs, resetKey }) {
  const [strategy, setStrategy] = useState(null);
  const [generatingStrategy, setGeneratingStrategy] = useState(false);
  const [showStrategyPanel, setShowStrategyPanel] = useState(false);
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [customVariables, setCustomVariables] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const [savingStrategy, setSavingStrategy] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (resetKey > 0) setStrategy(null);
  }, [resetKey]);

  const handleGenerateStrategy = async () => {
    setGeneratingStrategy(true);
    setShowStrategyPanel(false);
    try {
      const body = {};
      if (filters.category) body.category = filters.category;
      if (filters.category_l2) body.category_l2 = filters.category_l2;
      if (filters.category_l3) body.category_l3 = filters.category_l3;
      if (filters.supplier) body.supplier = filters.supplier;
      if (filters.region) body.region = filters.region;
      if (filters.country) body.country = filters.country;
      if (filters.sbu) body.sbu = filters.sbu;
      if (filters.fiscal_year) body.fiscal_year = filters.fiscal_year;
      if (filters.startDate) body.start_date = filters.startDate;
      if (filters.endDate) body.end_date = filters.endDate;
      body.strategy_variables = selectedVariables;
      body.custom_variables = customVariables;
      const res = await api.post('/ai/category-strategy', body);
      setStrategy(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate strategy');
    } finally {
      setGeneratingStrategy(false);
    }
  };

  const toggleVariable = (v) => {
    setSelectedVariables(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  const addCustomVariable = () => {
    const trimmed = customInput.trim();
    if (trimmed && !customVariables.includes(trimmed)) {
      setCustomVariables(prev => [...prev, trimmed]);
      setCustomInput('');
    }
  };

  const handleSaveStrategy = async () => {
    if (!strategy?.strategy || savingRef.current) return;
    savingRef.current = true;
    setSavingStrategy(true);
    try {
      await api.post('/ai/category-strategy/save', {
        strategy: strategy.strategy,
        filter_context: strategy.filter_context,
        variables_used: [...selectedVariables, ...customVariables],
        data_summary: strategy.data_summary,
      });
      toast.success('Strategy saved to Reports');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save strategy');
    } finally {
      savingRef.current = false;
      setSavingStrategy(false);
    }
  };

  return (
    <>
      {/* Strategy Generation Panel (Category Manager + Super Admin only) */}
      {canGenerateStrategy && (
      <Card className="border-primary/30" data-testid="strategy-generation-panel">
        <CardContent className="pt-4">
          {!showStrategyPanel ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">AI Category Strategy</p>
                <p className="text-xs text-muted-foreground">Generate an AI-powered strategy considering your data, selected variables, and current world affairs</p>
              </div>
              <Button
                onClick={() => setShowStrategyPanel(true)}
                disabled={generatingStrategy}
                data-testid="generate-strategy-button"
              >
                {generatingStrategy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {generatingStrategy ? 'Generating...' : 'Generate Strategy'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">Configure Strategy Variables</p>
                  <p className="text-xs text-muted-foreground">Select the factors you want the AI to consider when building your strategy</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowStrategyPanel(false)} data-testid="close-strategy-panel">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Preset Variables */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="preset-variables">
                {PRESET_VARIABLES.map(v => (
                  <button
                    key={v}
                    onClick={() => toggleVariable(v)}
                    className={`text-left text-xs px-3 py-2 rounded-md border transition-colors ${
                      selectedVariables.includes(v)
                        ? 'bg-primary/10 border-primary text-primary font-medium'
                        : 'border-border hover:bg-muted/50 text-muted-foreground'
                    }`}
                    data-testid={`variable-${v.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  >
                    {v}
                  </button>
                ))}
              </div>

              {/* Custom Variables */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Custom Considerations</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomVariable()}
                    placeholder="e.g. upcoming merger, Brexit impact, new supplier onboarding..."
                    className="flex-1 text-sm px-3 py-1.5 rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="custom-variable-input"
                  />
                  <Button variant="outline" size="sm" onClick={addCustomVariable} data-testid="add-custom-variable">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {customVariables.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {customVariables.map(v => (
                      <span key={v} className="inline-flex items-center gap-1 text-xs bg-secondary/50 text-secondary-foreground px-2 py-1 rounded-md">
                        {v}
                        <button onClick={() => setCustomVariables(prev => prev.filter(x => x !== v))} className="hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  {selectedVariables.length + customVariables.length} variable{selectedVariables.length + customVariables.length !== 1 ? 's' : ''} selected
                </p>
                <Button
                  onClick={handleGenerateStrategy}
                  disabled={generatingStrategy}
                  data-testid="confirm-generate-strategy"
                >
                  {generatingStrategy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {generatingStrategy ? 'Generating...' : 'Generate Strategy'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Generated Strategy Output — Below the generation panel */}
      {strategy && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <Card className="border-primary/30 bg-primary/5" data-testid="strategy-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <CardTitle className="text-base">AI Category Strategy</CardTitle>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{strategy.filter_context}</span>
                  <span>|</span>
                  <span>{cs}{strategy.data_summary?.total_spend?.toLocaleString()} across {strategy.data_summary?.transaction_count?.toLocaleString()} txns</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveStrategy}
                    disabled={savingStrategy}
                    className="text-xs ml-2"
                    data-testid="save-strategy-button"
                  >
                    {savingStrategy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Database className="w-3 h-3 mr-1" />}
                    {savingStrategy ? 'Saving...' : 'Save Strategy'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="prose prose-sm max-w-none leading-relaxed whitespace-pre-wrap" data-testid="strategy-content">
                {renderAILines(strategy.strategy)}
              </div>

              {/* Factors Considered - Collapsible */}
              <FactorsConsidered strategy={strategy} />

              {/* Potential Alternative Suppliers */}
              <AlternativeSuppliers strategy={strategy} />
            </CardContent>
          </Card>
        </motion.div>
      )}
    </>
  );
}
